/**
 * The search input bar for the sitewide Header.
 * User may either press enter to search for the current term,
 * or click an autocomplete result to be taken to the search page.
 * Once a search term is longer than 2 characters, will issue autocomplete
 * requests, and display the results in a dropdown.
 * Also contains a search upsell upon hovering over the down caret on the right side.
 */

import React, {
  useState,
  useEffect,
  useRef,
  KeyboardEvent,
  useCallback,
  RefObject,
} from 'react';
import styled from 'astroturf/react';
import throttle from 'lodash/throttle';

import { currentUser } from 'js/utils/user';
import AutoCompleteResults from './AutoCompleteResults';
import SearchUpsell from './SearchUpsell';
import $image_url from 'js/utils/image_url';
import useOnClickOutside from 'js/utils/hooks/useOnClickOutside';
import routes from 'sitewide-js/core_pre_webpack/routes';
import _SearchInput from "SearchBar-SearchInput.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/SearchBar/SearchBar.tsx?styleId=SearchInput";
import _SearchContainer from "SearchBar-SearchContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/SearchBar/SearchBar.tsx?styleId=SearchContainer";
import _SearchIcon from "SearchBar-SearchIcon.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/SearchBar/SearchBar.tsx?styleId=SearchIcon";
import _SearchUpsellWrapper from "SearchBar-SearchUpsellWrapper.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/SearchBar/SearchBar.tsx?styleId=SearchUpsellWrapper";
import _SearchUpsellIcon from "SearchBar-SearchUpsellIcon.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/SearchBar/SearchBar.tsx?styleId=SearchUpsellIcon";
import _DropdownContainer from "SearchBar-DropdownContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/SearchBar/SearchBar.tsx?styleId=DropdownContainer";


const SearchInput = /*#__PURE__*/styled("input", null, {
  displayName: "SearchInput",
  styles: _SearchInput
});

const SearchContainer = /*#__PURE__*/styled("div", null, {
  displayName: "SearchContainer",
  styles: _SearchContainer
});

const SearchIcon = /*#__PURE__*/styled("img", null, {
  displayName: "SearchIcon",
  styles: _SearchIcon
});

const SearchUpsellWrapper = /*#__PURE__*/styled("div", null, {
  displayName: "SearchUpsellWrapper",
  styles: _SearchUpsellWrapper
});

const SearchUpsellIcon = /*#__PURE__*/styled("img", null, {
  displayName: "SearchUpsellIcon",
  styles: _SearchUpsellIcon
});

const DropdownContainer = /*#__PURE__*/styled("div", null, {
  displayName: "DropdownContainer",
  styles: _DropdownContainer
});

const abortRequestIfOngoing = (abortController: RefObject<AbortController>) => {
  if (abortController.current != null) {
    abortController.current.abort();
    // @ts-expect-error ts-migrate(2540) FIXME: Cannot assign to 'current' because it is a read-on... Remove this comment to see the full error message
    abortController.current = null;
  }
};

export const MIN_AUTOCOMPLETE_LENGTH = 3;
const REQUEST_THROTTLE_MS = 1000;
const UPSELL_TIMEOUT_MS = 300;

type Props = {
  // initial value of searchTerm, for the search page
  initialQuery?: string; // defaults to '' when undefined or null
  focusForced?: boolean;
  inAnswersCohort?: boolean;
  showPremiumUpsells?: boolean;
};

const SearchBar = ({
  initialQuery,
  inAnswersCohort,
  showPremiumUpsells = true,
  ...props
}: Props) => {
  const [searchTerm, setSearchTerm] = useState(initialQuery ?? '');
  const [autoCompleteResults, setAutoCompleteResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);

  const containerEl = useRef<HTMLDivElement>(null);
  const inputEl = useRef<HTMLInputElement>(null);
  const prevAbortController = useRef<AbortController>(null);
  const upsellTimeout = useRef();

  useOnClickOutside(containerEl, () => setFocused(false));

  const searchParams: { q: string } & Record<string, string> = {
    q: searchTerm,
  };

  const viewAllResults = () => {
    // Redirect eligible users to Answers
    if (inAnswersCohort && (window as any).$controller_name !== 'search') {
      viewAnswers(searchTerm);
    } else {
      const encodedParams = Object.entries(searchParams).reduce(
        (result, [key, value]) => {
          result[key] = encodeURIComponent(value);
          return result;
        },
        {} as Record<string, string>,
      );
      window.location.href = routes.search_path(encodedParams);
    }
  };

  const viewAnswers = (query: string) => {
    window.location.href = routes.answers_path({
      q: query,
      query_source: 'search_bar',
      from_search: true,
    });
  };

  // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchAutoCompleteResults = useCallback(
    throttle(
      (query: string, abortController: RefObject<AbortController>) => {
        if (query.length >= MIN_AUTOCOMPLETE_LENGTH) {
          setLoading(true);

          // @ts-expect-error ts-migrate(2540) FIXME: Cannot assign to 'current' because it is a read-on... Remove this comment to see the full error message
          abortController.current = new AbortController();
          const url = routes.ac_search_path({ q: query, limit: 20 });

          fetch(url, { signal: abortController.current.signal })
            .then((res) => {
              if (!res.ok) {
                throw Error(res.statusText);
              }
              return res.json();
            })
            .then((data) =>
              setAutoCompleteResults(
                // 'Query' type is always rendered without need for api
                // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'result' implicitly has an 'any' type.
                data.results?.filter((result) => result.type !== 'Query'),
              ),
            )
            .catch(() => {})
            .finally(() => setLoading(false));
        } else {
          setAutoCompleteResults([]);
        }
      },
      REQUEST_THROTTLE_MS,
      { leading: true, trailing: true },
    ),
    [setLoading, setAutoCompleteResults],
  );

  useEffect(() => {
    // If the searchTerm changes, abort any ongoing requests
    abortRequestIfOngoing(prevAbortController);

    fetchAutoCompleteResults(searchTerm, prevAbortController);

    // Abort the request on unmount
    return () => {
      abortRequestIfOngoing(prevAbortController);
    };
    // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    if (props.focusForced) inputEl.current?.focus();
  }, [props.focusForced]);

  return (
    /* id for SearchPopup portal to attach */
    <SearchContainer id="nav--search-input" ref={containerEl}>
      <SearchIcon
        src={$image_url('icons/fa/search.svg')}
        style={{ width: 14 }}
      />
      <SearchInput
        ref={inputEl}
        type="text"
        defaultValue={searchTerm}
        placeholder={inAnswersCohort ? 'Ask a research question' : 'Search'}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setSearchTerm(event.target.value)
        }
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
          e.key === 'Enter' && searchTerm.length > 0 && viewAllResults()
        }
        onFocus={() => setFocused(true)}
      />
      <AutoCompleteResults
        show={focused}
        searchParams={searchParams}
        results={autoCompleteResults}
        loading={loading}
        viewAllResults={viewAllResults}
      />

      {(currentUser?.premium || showPremiumUpsells) && (
        <SearchUpsellWrapper
          onMouseEnter={() => {
            clearTimeout(upsellTimeout.current);
            setUpsellOpen(true);
          }}
          onMouseLeave={() =>
            // @ts-expect-error ts-migrate(2322) FIXME: Type 'Timeout' is not assignable to type 'undefine... Remove this comment to see the full error message
            (upsellTimeout.current = setTimeout(
              () => setUpsellOpen(false),
              UPSELL_TIMEOUT_MS,
            ))
          }
        >
          <SearchUpsellIcon src={$image_url('icons/fa/caret-down-solid.svg')} />
          <DropdownContainer>
            <SearchUpsell show={upsellOpen} />
          </DropdownContainer>
        </SearchUpsellWrapper>
      )}
    </SearchContainer>
  );
};

export default SearchBar;
